import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { WearablesApiConfiguration } from 'src/app/api/proxy/wearables/wearables-api-configuration';
import {
  profileSdkAuthTokenGet,
  profileSummaryPost,
  profileStatisticsGet,
  profileMemberGoalUpdatePost,
  profileWorkoutHistoryGet,
  helpSupportedDevicesPlatformGet
} from 'src/app/api/proxy/wearables/functions';
import {
  DeviceLinkModel,
  GenderType,
  MemberGoalModel,
  MemberStatistics,
  MetricType,
  Platform,
  ProfileSummaryRequestModel,
  SessionConfigResponseModel,
  SummaryItemModel,
  SummaryModel,
  WorkoutModel,
} from 'src/app/api/proxy/wearables/wearables-models';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { PromptService } from '../promtp-service/prompt.service';
import { SecureStorageService } from '../storage/secure-storage.service';
import { StorageKey } from '../storage/models/storage-key.enum';
import { EventService } from '../events/event.service';
import { environment } from 'src/environments/environment';
import { TranslatorService } from '../translator-service/translator.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { StorageService } from '../storage/storage.service';
import VivaScoreConnect from 'src/app/plugins/vivascore-connect/vivascore-connect-plugin';
import { Network } from '@capacitor/network';
import { NavController } from '@ionic/angular';
import { BaseApiService } from '../base-api-service/base-api.service';


@Injectable({
  providedIn: 'root',
})

export class WearableService extends BaseApiService {


  private _config: SessionConfigResponseModel | null = null;
  private NETWORK_TIMEOUT = 40000;
  private SUMAMRY_NETWORK_TIMEOUT = 60000;



  constructor(
    private http: HttpClient,
    private wearablesCfg: WearablesApiConfiguration,
    protected _PromptService: PromptService,
    private _TranslatorService: TranslatorService,
    private _myProfileService: MyProfileService,
    protected _navCtrl: NavController,
  ) {
    super(_PromptService);
  

  }

  ////////////////////////NEW Methods
  public get nativeDeviceProvider(): string {
    if (Capacitor.getPlatform() == 'android') {
      return "VivaConnectSDK-android";
    } else if (Capacitor.getPlatform() == 'ios') {
      return "VivaConnectSDK-ios";
    } else {
      throw new Error('Unsupported platform for native device string');
    }
  }

  private sdkTokenFecthed: Date | null = null;
  public async checkConnectSDK(): Promise<boolean> {


    if (Capacitor.getPlatform() === 'web') {
      return false; // nothing to do on web
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return false;
    }

    //if we were disconnected we have to reconnect with a new token
    const connected = await VivaScoreConnect.getConnected();
    if (!connected?.connected) {
      this.sdkTokenFecthed = null;
    } else {
      await SecureStorageService.set(SecureStorageKey.WearablePermissionPromptShown, 'true');
    }

    if (this.sdkTokenFecthed && this.sdkTokenFecthed.getTime() > Date.now() - 1000 * 60 * 60) {
      // If the token was fetched within the last hour
      // and the SDK Reported true
      // we can skip refreshing connection and return
      return false;
    }

    // Step one: get a token from the server
    const obs = profileSdkAuthTokenGet(this.http, this.wearablesCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'profileSdkAuthTokenGet');
    const result = httpResponse ? httpResponse.body : null;

    if (result && result.token && result.token.length > 0) {
      // Step two: configure the VivaScoreConnect plugin
      let serverURL = this.wearablesCfg.rootUrl;
      await VivaScoreConnect.configure({
        endpoint: serverURL,
        token: result.token,
        privacyPolicyURL: environment.privacyPolicy,
      });
      this.sdkTokenFecthed = new Date(); //remember that we fetched a thoken
    }

    //check if we have prompted for permissions before to trigger if we should show a popup
    const value = await SecureStorageService.get(SecureStorageKey.WearablePermissionPromptShown);
    const permissionPromptShown: boolean = value?.trim().toLowerCase() === 'true';

    //for IOS Prompt with the ios message for health kit
    if (Capacitor.getPlatform() === 'ios') {

      //only if we have not shown a prompt before and we are not connected. this will request the full list of permissions
      if (!connected?.connected && !permissionPromptShown) {
        await this._PromptService.showPermissionPopup(
          this._TranslatorService.translateText('wearable-service.messages.apple-health-kit-permission-request'),
          'assets/v3/apple-permissions.gif'
        );
        await SecureStorageService.set(SecureStorageKey.WearablePermissionPromptShown, 'true');
      }

      //always prompt for permissions regardless - this is to catch if the SDK supports new data types
      //the SDK will suppress the prompt if not required
      const status = await VivaScoreConnect.requestHealthKitPermissions();
      if (status?.status === 'authorized') {
        return true;
      } else {
        await this._PromptService.showError(
          this._TranslatorService.translateText('wearable-service.errors.apple-healthkit-permission-error')
        );
        return false;
      }
    }

    //now if Android do the same
    if (Capacitor.getPlatform() === 'android') {

      if (!connected?.connected && !permissionPromptShown) {

        var healthConnectEnabled = await VivaScoreConnect.isHealthConnectAvailable();
        if (!healthConnectEnabled?.available) {
          await this._PromptService.showError(this._TranslatorService.translateText('wearable-service.errors.device-type-error'));
          await Browser.open({
            url: 'https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata'
          });
          return false;
        }

       await this._PromptService.showPermissionPopup(
          this._TranslatorService.translateText('wearable-service.messages.google-health-connect-permission-request'),
          'assets/v3/android-permissions.gif'
        );
        await SecureStorageService.set(SecureStorageKey.WearablePermissionPromptShown, 'true');
      }

      const result = await VivaScoreConnect.requestHealthConnectPermissions();
      if (result?.success) {
        return true;
      } else {
        await this._PromptService.showError(
          this._TranslatorService.translateText('wearable-service.errors.google-request-all-permission-error')
        );
        return false;
      }
    }

    return false;
  }

  public async getSummaryStatic(): Promise<SummaryModel | null> {
    const mySummary = await SecureStorageService.get(StorageKey.WearablesYesterdaySummary);
    let model: SummaryModel | null = null;
    if (mySummary) {
      try {
        model = JSON.parse(mySummary);
      } catch {
        model = null;
      }
    }

    if (model) {
      EventService.wearableSummaryUpdated.emit(model);
    }
    return model;
  }

  public async getSummary(forced: boolean = false): Promise<SummaryModel | null> {
    let cacheTimestamp: Date | null = null;
    let model = await this.getSummaryStatic();

    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts) {
        cacheTimestamp = new Date(ts);
      }
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Go back 1 day
    yesterday.setHours(23, 59, 59); // Set to 23:59:59.999
    const yesterDayISO = yesterday.toISOString();

    if (model && cacheTimestamp && !forced) {
      const now = new Date();

      const isSameDay = now.toDateString() === cacheTimestamp.toDateString();
      const fetchHour = cacheTimestamp.getHours();
      const ageHours = (now.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

      // case 1: fetched on a previous day
      if ((!isSameDay) || (fetchHour < 10 && now.getHours() >= 10) || (fetchHour >= 10 && ageHours > 8)) {
        //we have to reload data as the cache is sale
      }
      else {
        return model;
      }
    }


    const myProfile = await this._myProfileService.GetProfile();

    const request: ProfileSummaryRequestModel = {
      summaryDate: yesterDayISO // Send in ISO string format
    };

    if (myProfile) {
      if (myProfile.dateOfBirth) {
        request.dateOfBirth = myProfile.dateOfBirth;
      }
      if (myProfile.weightInKg) {
        request.weightInKg = myProfile.weightInKg;
      }

      if (myProfile.gender) {
        request.gender = myProfile!.gender == 'Male' ? GenderType.Male : GenderType.Female;
      }


    }

    const obs = profileSummaryPost(this.http, this.wearablesCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.SUMAMRY_NETWORK_TIMEOUT, 'summary');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      model = result;

      (result as any).fetchedAt = new Date().toISOString();

      await SecureStorageService.set(StorageKey.WearablesYesterdaySummary, JSON.stringify(result));
      EventService.wearableSummaryUpdated.emit(model);
    }
    return model;
  }

  public async getMemberStats(forced: boolean): Promise<MemberStatistics | null> {
    const mySummary = await SecureStorageService.get(StorageKey.WearableStatisticsSummary);

    let cacheTimestamp: Date | null = null;
    let model: MemberStatistics | null = null;

    if (mySummary) {
      try {
        model = JSON.parse(mySummary);
      } catch {
        model = null;
      }
    }

    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts) {
        cacheTimestamp = new Date(ts);
      }
    }

    let summaryStat = await this.getSummaryStatic();

    if (model && summaryStat && summaryStat.items && model.latest) {

      var lastItem = model.latest[model.latest.length - 1];
      if (lastItem) {
        var metric = summaryStat.items.find((a) => a.metricType === MetricType.Steps);
        if (metric && lastItem.distanceSteps && metric.value && lastItem.distanceSteps != metric.value) {
          forced = true;
        }

        metric = summaryStat.items.find((a) => a.metricType === MetricType.ActiveTime);
        if (metric && lastItem.activityActiveSeconds && metric.value && lastItem.activityActiveSeconds != metric.value) {
          forced = true;
        }

        metric = summaryStat.items.find((a) => a.metricType === MetricType.CaloriesBurned);
        if (metric && lastItem.caloriesExpenditureKCal && metric.value && lastItem.caloriesExpenditureKCal != metric.value) {
          forced = true;
        }

        metric = summaryStat.items.find((a) => a.metricType === MetricType.HeartRate);
        if (metric && lastItem.heartRateAverageBPM && metric.value && lastItem.heartRateAverageBPM != metric.value) {
          forced = true;
        }

        metric = summaryStat.items.find((a) => a.metricType === MetricType.Spo2);
        if (metric && lastItem.oxygenationSaturationAveragePercentage && metric.value && lastItem.oxygenationSaturationAveragePercentage != metric.value) {
          forced = true;
        }

        metric = summaryStat.items.find((a) => a.metricType === MetricType.V02Max);
        if (metric && lastItem.oxygenationVO2Max && metric.value && lastItem.oxygenationVO2Max != metric.value) {
          forced = true;
        }

        metric = summaryStat.items.find((a) => a.metricType === MetricType.Sleep);
        if (metric && lastItem.sleep && metric.value && lastItem.sleep != metric.value) {
          forced = true;
        }


        metric = summaryStat.items.find((a) => a.metricType === MetricType.Stress);
        if (metric && lastItem.stressAverageLevel && metric.value && lastItem.stressAverageLevel != metric.value) {
          forced = true;
        }
      }
    }


    if (model && cacheTimestamp && !forced) {
      const now = new Date();

      const isSameDay = now.toDateString() === cacheTimestamp.toDateString();
      const fetchHour = cacheTimestamp.getHours();
      const ageHours = (now.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

      // case 1: fetched on a previous day
      if (!isSameDay || (fetchHour < 10 && now.getHours() >= 10) || (fetchHour >= 10 && ageHours > 8)) {
        //refresh as the cache is stale
      }
      // case: still valid
      else {
        return model;
      }
    }

    const obs2 = profileStatisticsGet(this.http, this.wearablesCfg.rootUrl);
    const httpResponse2 = await this.callApi(obs2, this.SUMAMRY_NETWORK_TIMEOUT, 'summary');
    const statsModel = httpResponse2 ? httpResponse2.body : null;
    if (statsModel) {
      model = statsModel;
      (model as any).fetchedAt = new Date().toISOString();
      await StorageService.set(StorageKey.WearableStatisticsSummary, JSON.stringify(model));
    }

    return model;
  }

  public async updateGoals(items: SummaryItemModel[]) {
    const model: MemberGoalModel[] = [];

    items.forEach((a) => {
      if (a.metricType && a.goalValue != null) {
        const itm: MemberGoalModel = {
          metricType: a.metricType, goalValue: a.goalValue
        };
        model.push(itm);
      }
    });

    const result = await this.callApi<any>(
      profileMemberGoalUpdatePost(this.http, this.wearablesCfg.rootUrl, { body: model }),
      this.NETWORK_TIMEOUT,
      'goalsSet',
    );
    if (!result) {
      return false;
    }
    await this.patchCachedSummaryGoals(items);
    return true;

  }

  private async patchCachedSummaryGoals(items: SummaryItemModel[]): Promise<void> {
    const mySummary = await SecureStorageService.get(StorageKey.WearablesYesterdaySummary);
    if (!mySummary) {
      return;
    }

    try {
      const summary = JSON.parse(mySummary) as SummaryModel;
      if (!summary.items?.length) {
        return;
      }

      for (const savedItem of items) {
        if (!savedItem.metricType || savedItem.goalValue == null) {
          continue;
        }
        const cachedItem = summary.items.find((item) => item.metricType === savedItem.metricType);
        if (cachedItem) {
          cachedItem.goalValue = savedItem.goalValue;
        }
      }

      await SecureStorageService.set(StorageKey.WearablesYesterdaySummary, JSON.stringify(summary));
    } catch {
      // Server save succeeded; ignore local cache patch failures.
    }
  }

  public async getWorkoutHistoryStatic(): Promise<WorkoutModel[] | null> {
    const mySummary = await SecureStorageService.get(StorageKey.WearableWorkoutHistory);
    let model: WorkoutModel[] | null = null;
    if (mySummary) {
      try {
        model = JSON.parse(mySummary);
      } catch {
        model = null;
      }
    }

    if (model) {
      EventService.wearableWorkoutHistoryUpdated.emit(model);
    }
    return model;
  }


  public async getWorkoutHistory(forced: boolean = false): Promise<WorkoutModel[] | null> {
    let model = await this.getWorkoutHistoryStatic();

    const lastWorkoutFetchStr = await SecureStorageService.get(StorageKey.WearableWorkoutHistoryFetched);
    var lastWorkoutFetch: Date | null = null;
    if (lastWorkoutFetchStr) {
      lastWorkoutFetch = new Date(lastWorkoutFetchStr);
    }


    if (model && lastWorkoutFetch && !forced) {
      const now = new Date();
      const ageHours = (now.getTime() - lastWorkoutFetch.getTime()) / (1000 * 60 * 60);

      if (ageHours < 1) {
        return model;
      }
    }

    const obs3 = profileWorkoutHistoryGet(this.http, this.wearablesCfg.rootUrl);
    const httpResponse3 = await this.callApi(obs3, this.SUMAMRY_NETWORK_TIMEOUT, 'summary');
    const result = httpResponse3 ? httpResponse3.body : null;

    if (result) {
      model = result;
      lastWorkoutFetch = new Date();
      await SecureStorageService.set(StorageKey.WearableWorkoutHistoryFetched, lastWorkoutFetch.toISOString());
      await SecureStorageService.set(StorageKey.WearableWorkoutHistory, JSON.stringify(result));
      EventService.wearableWorkoutHistoryUpdated.emit(model);
    }
    return model;
  }

  //Help page methods

  public async getHelpPageStatic(): Promise<DeviceLinkModel[] | null> {
    const mySummary = await SecureStorageService.get(StorageKey.DeviceHelpLinkData);
    let model: DeviceLinkModel[] | null = null;
    if (mySummary) {
      try {
        model = JSON.parse(mySummary);
      } catch {
        model = null;
      }
    }


    return model; 
  }

  public async getHelpPage(): Promise<DeviceLinkModel[] | null> {
    let model = await this.getHelpPageStatic();

    const lastFetchStr = await SecureStorageService.get(StorageKey.LastDeviceHelpLinkData);
    var lastFetch: Date | null = null;
    if (lastFetchStr) {
      lastFetch = new Date(lastFetchStr);
    }


    if (model && lastFetch) {
      const now = new Date();
      const ageHours = (now.getTime() - lastFetch.getTime()) / (1000 * 60 * 60 * 24);

      if (ageHours < 1) {
        return model;
      }
    }

    let pt: Platform = Platform.Android;
    if (Capacitor.getPlatform() == "ios")
      pt = Platform.IOs;

    const obs4 = helpSupportedDevicesPlatformGet(this.http, this.wearablesCfg.rootUrl, {
      platform: pt,
      includeImageBase64: true, // optional
    });
    const httpResponse4 = await this.callApi(obs4, this.SUMAMRY_NETWORK_TIMEOUT, 'summary');
    let result = httpResponse4 ? httpResponse4.body : null;

    model = result ?? model;

    //if we are on web fetch ios setup aswell
    if (Capacitor.getPlatform() == "web") {
      pt = Platform.IOs;

      const obs5 = helpSupportedDevicesPlatformGet(this.http, this.wearablesCfg.rootUrl, {
        platform: pt,
        includeImageBase64: true, // optional
      });
      const httpResponse5 = await this.callApi(obs5, this.SUMAMRY_NETWORK_TIMEOUT, 'summary');
      const webPlatform = httpResponse5 ? httpResponse5.body : null;

      if (result && webPlatform) {
        result = [...result, ...webPlatform];
      }
    }


    if (result) {
      lastFetch = new Date();
      await SecureStorageService.set(StorageKey.LastDeviceHelpLinkData, lastFetch.toISOString());
      await SecureStorageService.set(StorageKey.DeviceHelpLinkData, JSON.stringify(result));
    }
    return model;
  }

  /**
   * Get the timestamp of the last successfully sent record from VivaScoreConnect SDK.
   * Returns null if no records have been sent yet or if running on web platform.
   * @returns Date object or null
   */
  public async getLastRecordSent(): Promise<Date | null> {
    if (Capacitor.getPlatform() === 'web') {
      return null; // Not available on web
    }

    try {
      const result = await VivaScoreConnect.getLastRecordSent();
      if (result.timestamp) {
        return new Date(result.timestamp);
      }
      return null;
    } catch (error) {
      console.error('[WearableService] Error getting last record sent:', error);
      return null;
    }
  }
}


export interface WebConnectResult {
  success: boolean;
  refreshData: boolean;
}



